'use client';

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { CommunityPost } from './CommunityContent';
import { createClient } from '@/lib/local-api/client';
import { loadInteractions } from '@/lib/local-api/interaction-cache';
import { ChatBubbleLeftIcon, ShareIcon, HandThumbUpIcon, BookmarkIcon, PaperAirplaneIcon, PlayIcon, XMarkIcon, GlobeAltIcon, LockClosedIcon, UserPlusIcon, CheckIcon, EllipsisHorizontalIcon, NoSymbolIcon, BellSlashIcon, ChevronDownIcon, ChevronUpIcon, BoltIcon, TrashIcon } from '@heroicons/react/24/outline';
import { BookmarkIcon as BookmarkSolid } from '@heroicons/react/24/solid';
import Link from 'next/link';
import SignInPromptModal from '@/components/SignInPromptModal';
import BoostPostModal from '@/components/BoostPostModal';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const tagConfig = {
  General: { bg: 'bg-muted text-muted-foreground border-border', label: 'General' },
  Earning: { bg: 'bg-green-500/10 text-green-600 border-green-500/20', label: '💰 Earning' },
  'Lost Money': { bg: 'bg-red-500/10 text-red-600 border-red-500/20', label: '💸 Lost Money' },
  Warning: { bg: 'bg-amber-500/10 text-amber-600 border-amber-500/20', label: '⚠️ Warning' },
  Question: { bg: 'bg-blue-500/10 text-blue-600 border-blue-500/20', label: '❓ Question' },
  Discussion: { bg: 'bg-muted text-muted-foreground border-border', label: '💬 Discussion' },
};

const REACTIONS = [
  { key: 'like', emoji: '👍', label: 'Like', color: 'text-blue-500' },
  { key: 'love', emoji: '❤️', label: 'Love', color: 'text-red-500' },
  { key: 'haha', emoji: '😂', label: 'Haha', color: 'text-yellow-500' },
  { key: 'wow', emoji: '😮', label: 'Wow', color: 'text-yellow-500' },
  { key: 'sad', emoji: '😢', label: 'Sad', color: 'text-yellow-500' },
  { key: 'angry', emoji: '😡', label: 'Angry', color: 'text-orange-500' },
];

const CONTENT_PREVIEW_LIMIT = 180;

interface PostComment {
  id: string;
  author_id: string;
  content: string;
  created_at: string;
  user_profiles?: { full_name: string; role: string };
}

interface PostCardProps {
  post: CommunityPost & { visibility?: 'community' | 'public' };
  currentUserId?: string;
  sentRequests?: Set<string>;
  connections?: Set<string>;
  onSendRequest?: (receiverId: string) => void;
  processingRequest?: string | null;
}

function MediaGrid({ urls, types }: { urls: string[]; types: string[] }) {
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);

  if (!urls || urls.length === 0) return null;

  const count = urls.length;
  const gridClass =
    count === 1 ? 'grid-cols-1' :
    count === 2 ? 'grid-cols-2' :
    count === 3 ? 'grid-cols-3' : 'grid-cols-2';

  return (
    <>
      <div className={`grid ${gridClass} gap-0.5 overflow-hidden mt-1.5`}>
        {urls.slice(0, 4).map((url, i) => {
          const isVideo = types?.[i] === 'video';
          const isLast = i === 3 && count > 4;
          return (
            <div
              key={i}
              className={`relative cursor-pointer group overflow-hidden bg-muted ${isVideo && count === 1 ? 'aspect-video' : 'aspect-square'}`}
              onClick={() => setLightboxIdx(i)}
            >
              {isVideo ? (
                <>
                  <video src={url} className="w-full h-full object-cover" preload="metadata" muted />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/40 transition-colors">
                    <div className="w-10 h-10 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
                      <PlayIcon className="text-gray-800 ml-0.5" style={{ width: 18, height: 18 }} />
                    </div>
                  </div>
                </>
              ) : (
                <img src={url} alt={`Post media ${i + 1}`} className="absolute inset-0 w-full h-full object-cover group-hover:brightness-95 transition-all duration-200" />
              )}
              {isLast && (
                <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                  <span className="text-white text-2xl font-bold">+{count - 4}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {lightboxIdx !== null && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={() => setLightboxIdx(null)}>
          <button className="absolute top-4 right-4 text-white bg-white/10 hover:bg-white/20 rounded-full p-2 transition-colors" onClick={() => setLightboxIdx(null)}>
            <XMarkIcon style={{ width: 20, height: 20 }} />
          </button>
          <div className="max-w-4xl max-h-[90vh] w-full" onClick={(e) => e.stopPropagation()}>
            {types?.[lightboxIdx] === 'video' ? (
              <video src={urls[lightboxIdx]} controls autoPlay className="w-full max-h-[85vh] rounded-xl" />
            ) : (
              <img src={urls[lightboxIdx]} alt="Full size media" className="w-full max-h-[85vh] object-contain rounded-xl" />
            )}
          </div>
          {urls.length > 1 && (
            <div className="absolute bottom-6 flex gap-2">
              {urls.map((_, i) => (
                <button key={i} onClick={(e) => { e.stopPropagation(); setLightboxIdx(i); }} className={`w-2 h-2 rounded-full transition-colors ${i === lightboxIdx ? 'bg-white' : 'bg-white/40'}`} />
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
}

export default function PostCard({ post, currentUserId, sentRequests, connections, onSendRequest, processingRequest }: PostCardProps) {
  const [liked, setLiked] = useState(false);
  const [myReaction, setMyReaction] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [likeCount, setLikeCount] = useState(post.likes);
  const [commentCount, setCommentCount] = useState(post.comments);
  useEffect(() => { setLikeCount(post.likes); setCommentCount(post.comments); }, [post.likes, post.comments]);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState<PostComment[]>([]);
  const [commentText, setCommentText] = useState('');
  const [loadingComments, setLoadingComments] = useState(false);
  const [submittingComment, setSubmittingComment] = useState(false);
  const [likeLoading, setLikeLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [showReactions, setShowReactions] = useState(false);
  const [showSignIn, setShowSignIn] = useState(false);
  const [signInAction, setSignInAction] = useState('perform this action');
  const [showPostMenu, setShowPostMenu] = useState(false);
  const [isAuthorMuted, setIsAuthorMuted] = useState(false);
  const [isAuthorBlocked, setIsAuthorBlocked] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [showBoostModal, setShowBoostModal] = useState(false);
  const [contentExpanded, setContentExpanded] = useState(false);
  const [detailPanelOpen, setDetailPanelOpen] = useState(false);
  const [deletingPost, setDeletingPost] = useState(false);
  const reactionTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Pull live profile from AuthContext so name/avatar always reflects latest updates
  const { profile: authProfile, user: currentUser } = useAuth();

  // For the current user's own posts, override stale post author data with live profile
  const isOwnPost = currentUserId && post.authorId === currentUserId;
  const displayAuthor = isOwnPost && authProfile?.full_name ? authProfile.full_name : post.author;
  const displayAvatarUrl = isOwnPost && authProfile ? (authProfile.avatar_url || undefined) : (post as any).avatarUrl;
  const displayInitials = isOwnPost && authProfile?.full_name
    ? authProfile.full_name.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase()
    : post.avatar;

  const cfg = tagConfig[post.tag];
  const isConnected = connections?.has(post.authorId ?? '');
  const hasSentRequest = sentRequests?.has(post.authorId ?? '');
  const isProcessingConnect = processingRequest === post.authorId;
  const visibility = (post as any).visibility ?? 'community';

  const isLongContent = post.content.length > CONTENT_PREVIEW_LIMIT;
  const displayContent = isLongContent && !contentExpanded
    ? post.content.slice(0, CONTENT_PREVIEW_LIMIT).trimEnd() + '…'
    : post.content;

  const hasDetailPanel = likeCount > 0 || commentCount > 0 || post.shares > 0;

  useEffect(() => {
    let active = true;
    if (!currentUser) {
      setLiked(false); setMyReaction(null); setSaved(false);
      setIsAuthorMuted(false); setIsAuthorBlocked(false);
      return;
    }
    loadInteractions(currentUser.id, createClient()).then(({ likes, saves, mutes, blocks }) => {
      if (!active) return;
      const like = likes.find((row) => row.post_id === post.id);
      setLiked(!!like);
      setMyReaction(like?.reaction_type ?? null);
      setSaved(saves.some((row) => row.post_id === post.id));
      const muted = mutes.some((row) => row.muted_id === post.authorId);
      const blocked = blocks.some((row) => row.blocked_id === post.authorId);
      setIsAuthorMuted(muted);
      setIsAuthorBlocked(blocked);
      if (muted || blocked) setHidden(true);
    }).catch(() => { /* Keep current UI state when a read fails. */ });
    return () => { active = false; };
  }, [post.id, post.authorId, currentUser?.id]);

  useEffect(() => {
    const supabase = createClient();
    const channel = supabase
      .channel(`post_interactions_${post.id}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'post_likes', filter: `post_id=eq.${post.id}` }, () => {
        supabase.from('community_posts').select('likes').eq('id', post.id).single().then(({ data }) => { if (data) setLikeCount(data.likes ?? 0); });
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'post_comments', filter: `post_id=eq.${post.id}` }, () => {
        supabase.from('community_posts').select('comments').eq('id', post.id).single().then(({ data }) => { if (data) setCommentCount(data.comments ?? 0); });
        if (showComments) fetchComments();
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [post.id, showComments]);

  const fetchComments = useCallback(async () => {
    setLoadingComments(true);
    const supabase = createClient();
    const { data } = await supabase.from('post_comments').select('*, user_profiles(full_name, role)').eq('post_id', post.id).order('created_at', { ascending: true }).limit(50);
    if (data) setComments(data as PostComment[]);
    setLoadingComments(false);
  }, [post.id]);

  const handleToggleComments = () => {
    const next = !showComments;
    setShowComments(next);
    if (next && comments.length === 0) fetchComments();
  };

  const handleLike = async () => {
    if (!currentUser) { requireAuth('like posts', () => {}); return; }
    if (likeLoading) return;
    setLikeLoading(true);
    const supabase = createClient();
    if (liked) {
      await supabase.from('post_likes').delete().eq('post_id', post.id).eq('user_id', currentUser.id);
      setLiked(false); setMyReaction(null); setLikeCount((c) => Math.max(0, c - 1));
    } else {
      await supabase.from('post_likes').upsert(
        { post_id: post.id, user_id: currentUser.id, reaction_type: 'like' },
        { onConflict: 'post_id,user_id' }
      );
      setLiked(true); setMyReaction('like'); setLikeCount((c) => c + 1);
    }
    setLikeLoading(false);
  };

  const handleReaction = async (reactionKey: string) => {
    if (!currentUser) return;
    setShowReactions(false);
    setLikeLoading(true);
    const supabase = createClient();
    if (liked && myReaction === reactionKey) {
      // Toggle off same reaction
      await supabase.from('post_likes').delete().eq('post_id', post.id).eq('user_id', currentUser.id);
      setLiked(false); setMyReaction(null); setLikeCount((c) => Math.max(0, c - 1));
    } else if (liked) {
      // Change reaction type — update existing row
      await supabase
        .from('post_likes')
        .update({ reaction_type: reactionKey })
        .eq('post_id', post.id)
        .eq('user_id', currentUser.id);
      setMyReaction(reactionKey);
    } else {
      // New reaction
      await supabase.from('post_likes').upsert(
        { post_id: post.id, user_id: currentUser.id, reaction_type: reactionKey },
        { onConflict: 'post_id,user_id' }
      );
      setLiked(true); setMyReaction(reactionKey); setLikeCount((c) => c + 1);
    }
    setLikeLoading(false);
  };

  const handleSave = async () => {
    if (!currentUser) { requireAuth('save posts', () => {}); return; }
    if (saveLoading) return;
    setSaveLoading(true);
    const supabase = createClient();
    if (saved) {
      await supabase.from('post_saves').delete().eq('post_id', post.id).eq('user_id', currentUser.id);
      setSaved(false);
    } else {
      await supabase.from('post_saves').insert({ post_id: post.id, user_id: currentUser.id });
      setSaved(true);
    }
    setSaveLoading(false);
  };

  const handleSubmitComment = async () => {
    if (!currentUser || !commentText.trim() || submittingComment) return;
    setSubmittingComment(true);
    const supabase = createClient();
    const { error } = await supabase.from('post_comments').insert({ post_id: post.id, author_id: currentUser.id, content: commentText.trim() });
    if (!error) { setCommentText(''); setCommentCount((c) => c + 1); fetchComments(); }
    setSubmittingComment(false);
  };

  const handleShare = async () => {
    if (sharing) return;
    const url = `${window.location.origin}/community-network#post-${post.id}`;
    const shareData = { title: 'BizzBox post', url };
    if (!navigator.share || (navigator.canShare && !navigator.canShare(shareData))) {
      setShareUrl(url);
      return;
    }
    setSharing(true);
    try {
      // Invoke directly from the tap, before any network requests.
      await navigator.share(shareData);
    } catch (error) {
      if (!(error instanceof Error && error.name === 'AbortError')) setShareUrl(url);
      return;
    } finally { setSharing(false); }
    if (currentUser) {
      void fetch(`/api/community/posts/${post.id}/share`, { method: 'POST' }).catch(() => {});
    }
  };

  const copyShareLink = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Post link copied');
      setShareUrl(null);
      if (currentUser) void fetch(`/api/community/posts/${post.id}/share`, { method: 'POST' }).catch(() => {});
    } catch {
      toast('Select the link below to copy it');
    }
  };

  const handleBlockAuthor = async () => {
    if (!currentUser || !post.authorId) return;
    setShowPostMenu(false);
    const supabase = createClient();
    if (isAuthorBlocked) {
      await supabase.from('user_blocks').delete().eq('blocker_id', currentUser.id).eq('blocked_id', post.authorId);
      setIsAuthorBlocked(false); setHidden(false);
    } else {
      await supabase.from('user_blocks').upsert({ blocker_id: currentUser.id, blocked_id: post.authorId });
      setIsAuthorBlocked(true); setHidden(true);
    }
  };

  const handleMuteAuthor = async () => {
    if (!currentUser || !post.authorId) return;
    setShowPostMenu(false);
    const supabase = createClient();
    if (isAuthorMuted) {
      await supabase.from('user_mutes').delete().eq('muter_id', currentUser.id).eq('muted_id', post.authorId);
      setIsAuthorMuted(false); setHidden(false);
    } else {
      await supabase.from('user_mutes').upsert({ muter_id: currentUser.id, muted_id: post.authorId });
      setIsAuthorMuted(true); setHidden(true);
    }
  };

  const handleDeletePost = async () => {
    if (!currentUser || !isOwnPost) return;
    if (!confirm('Delete this post? This cannot be undone.')) return;
    setShowPostMenu(false);
    setDeletingPost(true);
    const supabase = createClient();
    await supabase.from('community_posts').delete().eq('id', post.id).eq('author_id', currentUser.id);
    setHidden(true);
    setDeletingPost(false);
  };

  const requireAuth = (action: string, callback: () => void) => {
    if (!currentUser) { setSignInAction(action); setShowSignIn(true); return; }
    callback();
  };

  const activeReaction = REACTIONS.find((r) => r.key === myReaction);
  const hasMedia = post.mediaUrls && post.mediaUrls.length > 0;

  if (hidden) {
    return (
      <div className="bg-card border border-border rounded-xl px-4 py-2.5 flex items-center justify-between gap-3">
        <p className="text-xs text-muted-foreground italic">
          {isAuthorBlocked ? 'Post from a blocked user hidden.' : 'Post from a muted user hidden.'}
        </p>
        <button onClick={() => setHidden(false)} className="text-xs text-[#1877f2] hover:underline font-medium flex-shrink-0">
          Show anyway
        </button>
      </div>
    );
  }

  return (
    <div id={`post-${post.id}`} className={`min-w-0 w-full max-w-full [overflow-wrap:anywhere] bg-card border rounded-xl overflow-hidden ${post.isBoosted ? 'border-amber-500/50 shadow-sm shadow-amber-500/10' : 'border-border'}`}>
      {showSignIn && <SignInPromptModal onClose={() => setShowSignIn(false)} action={signInAction} />}
      {showBoostModal && <BoostPostModal postId={post.id} sourceType="connect_post" onClose={() => setShowBoostModal(false)} />}
      {shareUrl && (
        <section aria-label="Share post" className="p-4 bg-muted border-b border-border space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold">Share post</h3>
            <button type="button" onClick={() => setShareUrl(null)} aria-label="Close sharing options" className="p-2 rounded-lg hover:bg-card">
              <XMarkIcon className="w-4 h-4" />
            </button>
          </div>
          <input aria-label="Post link" value={shareUrl} readOnly onFocus={(event) => event.target.select()} className="w-full rounded-lg border border-border bg-card px-3 py-2 text-sm" />
          <div className="flex gap-3">
            <button type="button" onClick={copyShareLink} className="px-3 py-2 rounded-lg bg-primary text-primary-foreground text-sm">Copy link</button>
            <a href={`mailto:?subject=BizzBox%20post&body=${encodeURIComponent(shareUrl)}`} className="px-3 py-2 rounded-lg border border-border text-sm">Email</a>
          </div>
        </section>
      )}

      {/* ── Boosted Badge ── */}
      {post.isBoosted && (
        <div className="px-3 pt-2 pb-0 flex items-center gap-1.5">
          <BoltIcon className="text-amber-500 flex-shrink-0" style={{ width: 11, height: 11 }} />
          <span className="text-[10px] font-bold text-amber-500 uppercase tracking-wide">Boosted</span>
        </div>
      )}

      {/* ── Compact Author Header ── */}
      <div className="px-3 pt-2.5 pb-1.5 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          {/* Avatar */}
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-xs font-bold text-white flex-shrink-0 overflow-hidden">
            {displayAvatarUrl ? (
              <img src={displayAvatarUrl} alt={`${displayAuthor} avatar`} className="w-full h-full object-cover" />
            ) : (
              displayInitials
            )}
          </div>
          {/* Name + meta inline */}
          <div className="min-w-0">
            <div className="flex items-center gap-1 flex-wrap">
              {post.authorId ? (
                <Link href={`/community-network/profile/${post.authorId}`} className="text-[13px] font-semibold text-foreground hover:text-[#1877f2] transition-colors leading-tight truncate">
                  {displayAuthor}
                </Link>
              ) : (
                <span className="text-[13px] font-semibold text-foreground leading-tight">{displayAuthor}</span>
              )}
              {post.verified && <span className="text-[10px] font-bold text-blue-500 flex-shrink-0">✓</span>}
              {isOwnPost && <span className="text-[9px] font-semibold text-primary bg-primary/10 px-1.5 py-0.5 rounded-full flex-shrink-0">You</span>}
            </div>
            <div className="flex items-center gap-1 mt-0">
              <span className="text-[11px] text-muted-foreground leading-tight">{post.date}</span>
              <span className="text-muted-foreground text-[10px]">·</span>
              {visibility === 'public'
                ? <GlobeAltIcon className="text-muted-foreground flex-shrink-0" style={{ width: 10, height: 10 }} />
                : <LockClosedIcon className="text-muted-foreground flex-shrink-0" style={{ width: 10, height: 10 }} />
              }
            </div>
          </div>
        </div>

        {/* Right controls */}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {currentUserId && !isOwnPost && post.authorId && onSendRequest && (
            isConnected ? (
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-green-600 bg-green-500/10 px-2 py-0.5 rounded-full border border-green-500/20">
                <CheckIcon style={{ width: 9, height: 9 }} /> Friends
              </span>
            ) : hasSentRequest ? (
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-muted-foreground bg-muted px-2 py-0.5 rounded-full border border-border">Requested</span>
            ) : (
              <button
                onClick={() => onSendRequest(post.authorId!)}
                disabled={isProcessingConnect}
                className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#1877f2] bg-[#1877f2]/10 hover:bg-[#1877f2] hover:text-white px-2 py-0.5 rounded-full border border-[#1877f2]/20 transition-all disabled:opacity-50"
              >
                {isProcessingConnect ? <span className="w-2 h-2 border border-current border-t-transparent rounded-full animate-spin" /> : <UserPlusIcon style={{ width: 9, height: 9 }} />}
                Add
              </button>
            )
          )}
          <div className="relative">
            <button onClick={() => setShowPostMenu((v) => !v)} className="w-7 h-7 rounded-full hover:bg-muted flex items-center justify-center transition-colors">
              <EllipsisHorizontalIcon className="text-muted-foreground" style={{ width: 16, height: 16 }} />
            </button>
            {showPostMenu && (
              <div className="absolute right-0 top-full mt-1 z-30 bg-card border border-border rounded-xl shadow-xl overflow-hidden min-w-[160px]">
                {isOwnPost && currentUser ? (
                  <>
                    <button
                      onClick={() => { setShowPostMenu(false); setShowBoostModal(true); }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-amber-600 hover:bg-amber-500/10 transition-colors text-left"
                    >
                      <BoltIcon style={{ width: 14, height: 14 }} />
                      Boost This Post
                    </button>
                    <button
                      onClick={handleDeletePost}
                      disabled={deletingPost}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-500 hover:bg-red-500/10 transition-colors text-left disabled:opacity-50"
                    >
                      <TrashIcon style={{ width: 14, height: 14 }} />
                      {deletingPost ? 'Deleting…' : 'Delete Post'}
                    </button>
                  </>
                ) : currentUser && !isOwnPost && post.authorId ? (
                  <>
                    <button onClick={() => requireAuth('mute users', handleMuteAuthor)} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-muted transition-colors text-left">
                      <BellSlashIcon style={{ width: 14, height: 14 }} className={isAuthorMuted ? 'text-amber-500' : 'text-muted-foreground'} />
                      {isAuthorMuted ? 'Unmute User' : 'Mute User'}
                    </button>
                    <button onClick={() => requireAuth('block users', handleBlockAuthor)} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-500 hover:bg-red-500/10 transition-colors text-left">
                      <NoSymbolIcon style={{ width: 14, height: 14 }} />
                      {isAuthorBlocked ? 'Unblock User' : 'Block User'}
                    </button>
                  </>
                ) : (
                  <div className="px-3 py-2 text-xs text-muted-foreground">No options available</div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Content Area ── */}
      <div className="px-3 pb-1.5">
        {/* Tag + project mention row */}
        <div className="flex items-center gap-1.5 mb-1.5 flex-wrap">
          <span className={`inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full border ${cfg.bg}`}>
            {cfg.label}
          </span>
          {post.projectMention && (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#1877f2] bg-[#1877f2]/10 px-2 py-0.5 rounded-full border border-[#1877f2]/20">
              <span className="w-1.5 h-1.5 rounded-full bg-[#1877f2]" />
              {post.projectMention}
            </span>
          )}
        </div>

        {/* Content preview with expand */}
        {post.sourceGroupId && (
          <Link href={`/community-groups/${encodeURIComponent(post.sourceGroupId)}`} className="inline-block mb-2 text-xs font-semibold text-primary hover:underline">
            View original group
          </Link>
        )}
        <p className="text-[13px] text-foreground leading-relaxed whitespace-pre-wrap [overflow-wrap:anywhere]">
          {displayContent}
          {isLongContent && !contentExpanded && (
            <button onClick={() => setContentExpanded(true)} className="ml-1 text-[#1877f2] text-[12px] font-medium hover:underline">
              See more
            </button>
          )}
          {isLongContent && contentExpanded && (
            <button onClick={() => setContentExpanded(false)} className="ml-1 text-[#1877f2] text-[12px] font-medium hover:underline">
              See less
            </button>
          )}
        </p>
      </div>

      {/* ── Media ── */}
      {hasMedia && (
        <div className="px-3 pb-1.5">
          <MediaGrid urls={post.mediaUrls!} types={post.mediaTypes ?? []} />
        </div>
      )}

      {/* ── Collapsible Detail Panel ── */}
      {hasDetailPanel && (
        <div className="px-3">
          <button
            onClick={() => setDetailPanelOpen((v) => !v)}
            className="w-full flex items-center justify-between py-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors"
          >
            <div className="flex items-center gap-2">
              {likeCount > 0 && (
                <span className="flex items-center gap-1">
                  <span className="w-4 h-4 rounded-full bg-[#1877f2] flex items-center justify-center text-[9px] ring-1 ring-card">👍</span>
                  {likeCount.toLocaleString()}
                </span>
              )}
              {commentCount > 0 && <span>{commentCount} comment{commentCount !== 1 ? 's' : ''}</span>}
              {post.shares > 0 && <span>{post.shares} shares</span>}
            </div>
            {detailPanelOpen
              ? <ChevronUpIcon style={{ width: 12, height: 12 }} />
              : <ChevronDownIcon style={{ width: 12, height: 12 }} />
            }
          </button>

          {detailPanelOpen && (
            <div className="pb-1.5 pt-0.5 border-t border-border/30">
              <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
                {likeCount > 0 && (
                  <div className="flex items-center gap-1">
                    <div className="flex -space-x-1">
                      <span className="w-4 h-4 rounded-full bg-[#1877f2] flex items-center justify-center text-[9px] ring-1 ring-card">👍</span>
                      {(post.reactions as any)?.heart > 0 && <span className="w-4 h-4 rounded-full bg-red-500 flex items-center justify-center text-[9px] ring-1 ring-card">❤️</span>}
                      {(post.reactions as any)?.fire > 0 && <span className="w-4 h-4 rounded-full bg-orange-500 flex items-center justify-center text-[9px] ring-1 ring-card">🔥</span>}
                    </div>
                    <span>{likeCount.toLocaleString()} reactions</span>
                  </div>
                )}
                {commentCount > 0 && (
                  <button onClick={handleToggleComments} className="hover:underline">
                    {commentCount} comment{commentCount !== 1 ? 's' : ''}
                  </button>
                )}
                {post.shares > 0 && <span>{post.shares} shares</span>}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Action Bar: Like / Comment / Share / Boost ── */}
      <div className="flex items-center px-1 py-0.5 border-t border-border/40">
        {/* Like */}
        <div className="relative flex-1">
          <button
            onMouseEnter={() => {
              if (reactionTimeout.current) clearTimeout(reactionTimeout.current);
              reactionTimeout.current = setTimeout(() => setShowReactions(true), 400);
            }}
            onMouseLeave={() => {
              if (reactionTimeout.current) clearTimeout(reactionTimeout.current);
              reactionTimeout.current = setTimeout(() => setShowReactions(false), 300);
            }}
            onClick={() => requireAuth('like posts', handleLike)}
            disabled={likeLoading}
            className={`w-full flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs font-semibold transition-all duration-150 disabled:opacity-50 hover:bg-muted ${
              liked ? (activeReaction?.color ?? 'text-[#1877f2]') : 'text-muted-foreground'
            }`}
          >
            {liked && activeReaction ? (
              <span className="text-sm leading-none">{activeReaction.emoji}</span>
            ) : (
              <HandThumbUpIcon style={{ width: 16, height: 16 }} />
            )}
            <span>{liked && activeReaction ? activeReaction.label : 'Like'}</span>
          </button>

          {showReactions && currentUser && (
            <div
              className="absolute bottom-full left-0 mb-1.5 z-30"
              onMouseEnter={() => { if (reactionTimeout.current) clearTimeout(reactionTimeout.current); setShowReactions(true); }}
              onMouseLeave={() => { if (reactionTimeout.current) clearTimeout(reactionTimeout.current); reactionTimeout.current = setTimeout(() => setShowReactions(false), 300); }}
            >
              <div className="flex items-center gap-1 bg-card border border-border rounded-full px-2.5 py-1.5 shadow-xl">
                {REACTIONS.map((r) => (
                  <button key={r.key} onClick={() => handleReaction(r.key)} title={r.label} className={`text-xl hover:scale-125 transition-transform duration-150 leading-none ${myReaction === r.key ? 'scale-125' : ''}`}>
                    {r.emoji}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Comment */}
        <button
          onClick={handleToggleComments}
          className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs font-semibold transition-all duration-150 hover:bg-muted ${showComments ? 'text-[#1877f2]' : 'text-muted-foreground'}`}
        >
          <ChatBubbleLeftIcon style={{ width: 16, height: 16 }} />
          Comment
        </button>

        {/* Share */}
        <button onClick={handleShare} disabled={sharing} className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs font-semibold text-muted-foreground hover:bg-muted transition-all duration-150 disabled:opacity-50">
          <ShareIcon style={{ width: 16, height: 16 }} />
          Share
        </button>

        {/* Boost — own posts only */}
        {isOwnPost && currentUser ? null : (
          /* Save — other users' posts */
          <button
            onClick={() => requireAuth('save posts', handleSave)}
            disabled={saveLoading}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-150 disabled:opacity-50 hover:bg-muted ${saved ? 'text-amber-500' : 'text-muted-foreground'}`}
          >
            {saved ? <BookmarkSolid style={{ width: 16, height: 16 }} /> : <BookmarkIcon style={{ width: 16, height: 16 }} />}
          </button>
        )}
      </div>

      {/* ── Comments Section ── */}
      {showComments && (
        <div className="px-3 pb-3 pt-1.5 space-y-2.5 border-t border-border/30">
          {currentUser ? (
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-[10px] flex-shrink-0">
                {(currentUser.user_metadata?.full_name || currentUser.email || 'U').split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1 flex items-center gap-2 bg-muted rounded-full px-3 py-1.5">
                <input
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmitComment(); } }}
                  placeholder="Write a comment…"
                  className="min-w-0 flex-1 bg-transparent text-[13px] text-foreground placeholder:text-muted-foreground focus:outline-none"
                />
                <button onClick={handleSubmitComment} disabled={!commentText.trim() || submittingComment} className="text-[#1877f2] hover:text-[#166fe5] disabled:opacity-40 transition-colors">
                  <PaperAirplaneIcon style={{ width: 14, height: 14 }} />
                </button>
              </div>
            </div>
          ) : (
            <button onClick={() => requireAuth('comment on posts', () => {})} className="w-full text-xs text-[#1877f2] hover:underline text-center py-1 font-medium">
              Sign in to comment
            </button>
          )}

          {loadingComments ? (
            <div className="flex justify-center py-2">
              <div className="w-4 h-4 border-2 border-[#1877f2] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : comments.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-1">No comments yet. Be the first!</p>
          ) : (
            <div className="space-y-2 max-h-56 overflow-y-auto">
              {comments.map((c) => {
                const name = c.user_profiles?.full_name || 'Member';
                const initials = name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase();
                const timeAgo = new Date(c.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                return (
                  <div key={c.id} className="flex items-start gap-2">
                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-[9px] font-bold text-white flex-shrink-0">
                      {initials}
                    </div>
                    <div className="min-w-0 flex-1 bg-muted rounded-2xl px-3 py-1.5">
                      <div className="flex flex-wrap items-center gap-1.5 mb-0.5">
                        <span className="text-[12px] font-bold text-foreground">{name}</span>
                        <span className="text-[10px] text-muted-foreground">{timeAgo}</span>
                      </div>
                      <p className="text-[13px] text-foreground leading-relaxed">{c.content}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
